import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiFetch, type RequestOptions } from "../../lib/api-client";

/**
 * The accounts-rbac module's routes (Memex templates/modules/accounts-rbac
 * 0.3.0, src/routes.ts), mounted by the API under `/api/v1/access`.
 *
 * Hand-typed on purpose and kept to what the screens read: the module's
 * routes are not in the tool's OpenAPI contract yet, and the shapes below are
 * the module's `service.ts` replies field for field. Mirrors Memex's own
 * client (frontend/src/api/access.ts, admin.ts).
 *
 * Every failure is the api-client's error: `message` is the module's German
 * `error` (or an RFC 7807 `detail`), `body` the raw reply - read with
 * `readFailure` (./failure.ts).
 */

const ACCESS = "/access";

// ------------------------------------------------------------------ shapes

export type AccountState = "unconfirmed" | "active" | "retired";

/**
 * One account as the viewer may see it ([F-030]): another person shows only
 * `id` and `display_name`; the administration and the person themself also
 * get address, roles and state.
 */
export interface AccountView {
  id: string;
  display_name: string;
  email?: string;
  roles?: string[];
  is_admin?: boolean;
  state?: AccountState;
}

/** GET /access - what a surface asks first. */
export interface AccessState {
  authenticated: boolean;
  method: "local" | "oidc";
  account?: AccountView;
  waiting?: boolean;
  elevated?: boolean;
  elevated_until?: string | null;
  /** The absolute end of the session ([NF-008] SESSION_MAX), fixed at login. */
  session_ends_at?: string;
  /** SESSION_WARNING_LEAD in seconds. */
  session_warning_lead?: number;
  /** SESSION_IDLE in seconds; null when the deployment has no idle limit. */
  session_idle_seconds?: number | null;
}

/** GET /access/me - the own account. */
export interface OwnAccount {
  account: AccountView;
  display_name: string;
  elevated: boolean;
  elevated_until: string | null;
  session_id: string;
  session_ends_at: string;
  session_warning_lead: number;
}

/** A login session ([DO-013]); `current` only in the own list. */
export interface LoginSession {
  id: string;
  account_id: string;
  created_at: string;
  expires_at: string;
  revoked_at: string | null;
  elevated_until: string | null;
  last_seen_at: string;
  client_ip: string;
  user_agent: string;
  current?: boolean;
}

/** One journal entry ([NF-017]): ids only, the actor resolved to a name. */
export interface AuditEvent {
  id: string;
  at: string;
  actor_id: string | null;
  actor: string;
  action: string;
  subject_kind: string;
  subject_id: string;
  details: Record<string, unknown>;
}

export interface RoleDef {
  id: string;
  label: string;
}

export type MatrixCell = "yes" | "no" | "own";

export interface MatrixRow {
  id: string;
  label: string;
  relation: string | null;
  cells: Record<string, MatrixCell>;
}

/** GET /access/admin/status - the Festlegungen in force ([F-025], [AC-045]). */
export interface AccessStatus {
  module: string;
  version: string;
  authentication: {
    method: string;
    accountCreation: string;
    firstLoginRoles: string[];
  };
  session: {
    absoluteSeconds: number;
    end: string;
    absoluteConfigurable: boolean;
    idleSeconds: number | null;
    warningLeadSeconds: number;
  };
  protection: {
    breakGlass: string;
    stepUp: string;
    stepUpExtra: string | null;
  };
  authorization: {
    scope: string;
    scopeObject: string | null;
    roles: RoleDef[];
    administration: string;
    adminRole: string | null;
    groups: string;
    visibility: string;
    relations: { id: string; meaning: string }[];
    matrix: MatrixRow[];
    isAdministrator: boolean;
  };
  lifecycle: { removal: string };
  /** NF-019: name -> effective value and where it came from (secrets masked). */
  parameters: Record<string, { value: string | number | null; origin: "environment" | "default" }>;
}

export interface LoginReply {
  ok?: boolean;
  /** The system account continues with the code from its mailbox ([F-031]). */
  next?: "code";
  session_ends_at?: string;
}

export interface CreateAccountReply {
  account: AccountView;
  invitation_sent: boolean;
}

export interface AccountPatch {
  roles?: string[];
  state?: "active" | "retired";
  display_name?: string;
  /** The code the system mailbox received ([F-032]). */
  authorization_code?: string;
}

// ------------------------------------------------------------ the requests

const json = (method: string, body?: unknown): RequestInit =>
  body === undefined ? { method } : { method, body: JSON.stringify(body) };

/** Login-screen requests: their 401 is an answer, not a lost session. */
const anonymous: RequestOptions = { anonymous: true };

export const accessApi = {
  state: (options: RequestOptions = {}) => apiFetch<AccessState>(`${ACCESS}/access`, undefined, options),
  login: (email: string, password: string) =>
    apiFetch<LoginReply>(`${ACCESS}/login`, json("POST", { email, password }), anonymous),
  loginCode: (code: string) => apiFetch<{ ok: boolean }>(`${ACCESS}/login/code`, json("POST", { code }), anonymous),
  firstLogin: (body: { account: string; secret: string; password: string; display_name: string }) =>
    apiFetch<{ ok: boolean }>(`${ACCESS}/first-login`, json("POST", body), anonymous),
  resetRequest: (email: string) => apiFetch<{ ok: boolean }>(`${ACCESS}/reset/request`, json("POST", { email }), anonymous),
  resetConfirm: (body: { email: string; code: string; password: string }) =>
    apiFetch<{ ok: boolean }>(`${ACCESS}/reset/confirm`, json("POST", body), anonymous),

  me: () => apiFetch<OwnAccount>(`${ACCESS}/me`),
  mySessions: () => apiFetch<LoginSession[]>(`${ACCESS}/me/sessions`),
  revokeMySession: (id: string) => apiFetch<{ ok: boolean }>(`${ACCESS}/me/sessions/${encodeURIComponent(id)}`, json("DELETE")),
  elevateRequest: () => apiFetch<{ ok: boolean; already?: boolean }>(`${ACCESS}/elevate/request`, json("POST")),
  elevate: (code: string) => apiFetch<{ ok: boolean; elevated_until: string }>(`${ACCESS}/elevate`, json("POST", { code })),
  changeDisplayName: (displayName: string) =>
    apiFetch<{ display_name: string }>(`${ACCESS}/me/display-name`, json("PATCH", { display_name: displayName })),
  changePassword: (password: string) => apiFetch<{ ok: boolean }>(`${ACCESS}/me/password`, json("POST", { password })),
  emailRequest: (newEmail: string) => apiFetch<{ ok: boolean }>(`${ACCESS}/me/email/request`, json("POST", { new_email: newEmail })),
  emailConfirm: (body: { new_email: string; code_old: string; code_new: string }) =>
    apiFetch<{ ok: boolean; email: string }>(`${ACCESS}/me/email/confirm`, json("POST", body)),

  adminAccounts: () => apiFetch<AccountView[]>(`${ACCESS}/admin/accounts`),
  createAccount: (body: { email: string; roles: string[] }) =>
    apiFetch<CreateAccountReply>(`${ACCESS}/admin/accounts`, json("POST", body)),
  updateAccount: (id: string, patch: AccountPatch) =>
    apiFetch<{ account: AccountView }>(`${ACCESS}/admin/accounts/${encodeURIComponent(id)}`, json("PATCH", patch)),
  resendInvite: (id: string) => apiFetch<{ ok: boolean }>(`${ACCESS}/admin/accounts/${encodeURIComponent(id)}/invite`, json("POST")),
  accountSessions: (id: string) => apiFetch<LoginSession[]>(`${ACCESS}/admin/accounts/${encodeURIComponent(id)}/sessions`),
  revokeSession: (id: string) => apiFetch<{ ok: boolean }>(`${ACCESS}/admin/sessions/${encodeURIComponent(id)}`, json("DELETE")),
  recoverRequest: (id: string, newEmail: string) =>
    apiFetch<{ ok: boolean }>(`${ACCESS}/admin/accounts/${encodeURIComponent(id)}/recover/request`, json("POST", { new_email: newEmail })),
  recoverConfirm: (id: string, body: { new_email: string; code_system: string; code_new: string }) =>
    apiFetch<{ ok: boolean; email: string }>(`${ACCESS}/admin/accounts/${encodeURIComponent(id)}/recover/confirm`, json("POST", body)),
  journal: (filter: { kind?: string; id?: string; limit?: number } = {}) => {
    const query = new URLSearchParams();
    if (filter.kind) query.set("kind", filter.kind);
    if (filter.id) query.set("id", filter.id);
    if (filter.limit) query.set("limit", String(filter.limit));
    const suffix = query.toString();
    return apiFetch<AuditEvent[]>(`${ACCESS}/admin/journal${suffix ? `?${suffix}` : ""}`);
  },
  status: () => apiFetch<AccessStatus>(`${ACCESS}/admin/status`),
};

// --------------------------------------------------------------- the hooks

/**
 * Everything of the module sits under ["access"], so a login or logout -
 * which clears the cache - and an elevation - which invalidates ["access"] -
 * reach all of it.
 */
export const accessKeys = {
  all: ["access"] as const,
  state: ["access", "state"] as const,
  me: ["access", "me"] as const,
  mySessions: ["access", "me", "sessions"] as const,
  admin: ["access", "admin"] as const,
  accounts: ["access", "admin", "accounts"] as const,
  accountSessions: (id: string) => ["access", "admin", "accounts", id, "sessions"] as const,
  journal: (kind: string, id: string) => ["access", "admin", "journal", kind, id] as const,
  status: ["access", "admin", "status"] as const,
};

/** How often the session layer looks at its own session, in the background. */
export const SESSION_POLL_MS = 60_000;

/**
 * The session as the module sees it ([NF-008]) - polled with `X-Background`,
 * so looking never counts as activity and a page left open still goes idle.
 */
export function useAccessState(enabled: boolean) {
  return useQuery({
    queryKey: accessKeys.state,
    queryFn: () => accessApi.state({ background: true }),
    enabled,
    refetchInterval: SESSION_POLL_MS,
    refetchOnWindowFocus: true,
    staleTime: 0,
  });
}

export function useOwnAccount() {
  return useQuery({ queryKey: accessKeys.me, queryFn: accessApi.me });
}

export function useOwnSessions() {
  return useQuery({ queryKey: accessKeys.mySessions, queryFn: accessApi.mySessions });
}

export function useRevokeOwnSession() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: accessApi.revokeMySession,
    onSettled: () => queryClient.invalidateQueries({ queryKey: accessKeys.mySessions }),
  });
}

export function useAdminAccounts() {
  return useQuery({ queryKey: accessKeys.accounts, queryFn: accessApi.adminAccounts });
}

export function useAccountSessions(id: string | null) {
  return useQuery({
    queryKey: accessKeys.accountSessions(id ?? ""),
    queryFn: () => accessApi.accountSessions(id ?? ""),
    enabled: id !== null,
  });
}

/** A 403 means the caller is not the administration; no retry for that. */
export function useAccessStatus(enabled = true) {
  return useQuery({
    queryKey: accessKeys.status,
    queryFn: accessApi.status,
    enabled,
    staleTime: 5 * 60_000,
    retry: false,
  });
}

export function useJournal(filter: { kind: string; id: string }) {
  return useQuery({
    queryKey: accessKeys.journal(filter.kind, filter.id),
    queryFn: () => accessApi.journal({ kind: filter.kind || undefined, id: filter.id || undefined, limit: 500 }),
  });
}

/**
 * Whether the chat delivery of reminders is configured ([F-016]), for the
 * status page. The route belongs to the API's system area; a 404 (not there
 * yet) hides the line rather than failing the page.
 */
export function useChatConfigured(enabled: boolean) {
  return useQuery({
    queryKey: ["system", "chat"],
    queryFn: () => apiFetch<{ configured: boolean }>("/system/chat"),
    enabled,
    retry: false,
    staleTime: 5 * 60_000,
  });
}

/** After an account change: the module's lists and the tool's directories. */
export function useInvalidateAccounts(): () => Promise<void> {
  const queryClient = useQueryClient();
  return async () => {
    await queryClient.invalidateQueries({ queryKey: accessKeys.admin });
    // Names and states feed the Owner/Bearbeiter pickers (features/users).
    await queryClient.invalidateQueries({ queryKey: ["users"] });
  };
}
